/**
 * F.6.2 · Tab "Modifiche Contrattuali" nel dettaglio Contratto.
 * Timeline cronologica + azioni "Nuova variazione quantità" / "Nuova lavorazione".
 *
 * F.6.2A · "Nuova lavorazione" NON crea più un'Offerta vuota up-front:
 * apre il wizard Offerta esistente con parent_subcontract_id + supplier
 * pre-compilati. L'Offerta viene creata SOLO al submit finale del wizard.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Ruler, FilePlus2, GitCommit, RefreshCw, FileText, Download, ExternalLink } from 'lucide-react';
import {
  listModifications, getModification, listRevisionSnapshots,
  downloadModificationPdf, previewModificationHtmlUrl,
  MODIFICATION_KIND_LABELS, MODIFICATION_STATUS_LABELS, MODIFICATION_STATUS_COLORS,
  fmtEUR, fmtDate,
} from '../../lib/contractModificationApi';
import QuantityVariationWizard from './QuantityVariationWizard';
import SubcontractOfferDetail from '../subcontract-offer/SubcontractOfferDetail';
import { SubcontractOfferWizard } from '../subcontract-offer/SubcontractOfferWizard';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => {
  const t = localStorage.getItem('token');
  return { headers: t ? { Authorization: `Bearer ${t}` } : {} };
};

const ContractModificationsTab = ({ subcontract, onSubcontractChanged }) => {
  const [mods, setMods] = useState([]);
  const [snaps, setSnaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qvOpen, setQvOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  // F.6.2A · Wizard "Nuova lavorazione" (Offerta EXTRA) — nessuna bozza pre-creata.
  const [newWorkWizardOpen, setNewWorkWizardOpen] = useState(false);
  const [newWorkProject, setNewWorkProject] = useState(null);
  const [openingNewWork, setOpeningNewWork] = useState(false);
  // Dopo la creazione dell'Offerta apriamo il detail per portarla a contratualizzazione.
  const [newWorkOfferId, setNewWorkOfferId] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [m, s] = await Promise.all([
        listModifications(subcontract.id),
        listRevisionSnapshots(subcontract.id),
      ]);
      setMods((m || []).sort((a, b) => new Date(a.created_at) - new Date(b.created_at)));
      setSnaps(s || []);
    } catch (e) {
      toast.error('Errore caricamento modifiche');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (subcontract?.id) reload(); }, [subcontract?.id]);

  // F.6.4 · gestione download PDF con anti-doppio-click per-row.
  const [pdfBusyId, setPdfBusyId] = useState(null);
  const handleDownloadPdf = async (mod, ev) => {
    if (ev) { ev.stopPropagation(); ev.preventDefault(); }
    if (pdfBusyId) return; // blocca doppio click / concorrenza
    if (mod.status !== 'effective') {
      toast.error('PDF disponibile solo per modifiche efficaci');
      return;
    }
    setPdfBusyId(mod.id);
    try {
      const filename = await downloadModificationPdf(mod.id, mod.modification_number);
      toast.success(`PDF scaricato · ${filename}`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data;
      const msg = (detail && (detail.detail?.message || detail.message)) || e?.message || 'Errore download PDF';
      if (status === 400) toast.error('Impossibile scaricare: modifica non efficace.');
      else if (status === 404) toast.error('Modifica non trovata.');
      else if (status === 401 || status === 403) toast.error('Sessione scaduta o non autorizzata.');
      else toast.error(typeof msg === 'string' ? msg : 'Errore download PDF');
    } finally {
      setPdfBusyId(null);
    }
  };

  const openNewWorkWizard = async () => {
    if (openingNewWork) return;
    setOpeningNewWork(true);
    try {
      // Fetch on-demand del progetto: serve al wizard Offerta.
      const r = await axios.get(`${API}/api/projects/${subcontract.project_id}`, auth());
      setNewWorkProject(r.data);
      setNewWorkWizardOpen(true);
    } catch (e) {
      toast.error('Impossibile caricare i dati del progetto');
    } finally {
      setOpeningNewWork(false);
    }
  };

  const handleNewWorkOfferCreated = (offer) => {
    setNewWorkWizardOpen(false);
    setNewWorkProject(null);
    setNewWorkOfferId(offer.id);
    toast.success(`Offerta ${offer.offer_number} creata. Completala per generare la modifica.`);
  };

  const totalCurrent = Number(subcontract?.total_amount_current ?? subcontract?.total_amount_net ?? 0);
  const totalOriginal = Number(subcontract?.total_amount_net ?? 0);
  const canModify = ['bozza', 'pronto_per_firma', 'firmato'].includes(subcontract?.status);

  return (
    <div className="space-y-4" data-testid="contract-modifications-tab">
      {/* Riepilogo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Revisione corrente" value={<Badge className="bg-teal-100 text-teal-800 border-teal-400" data-testid="cm-current-revision">Rev.{subcontract?.contract_revision ?? 1}</Badge>} />
        <SummaryCard label="Importo attuale" value={<span data-testid="cm-total-current">{fmtEUR(totalCurrent)}</span>} />
        <SummaryCard label="Importo originario" value={fmtEUR(totalOriginal)} />
        <SummaryCard label="Modifiche efficaci" value={<span data-testid="cm-effective-count">{mods.filter(m => m.status === 'effective').length}</span>} />
      </div>

      {/* Azioni */}
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => setQvOpen(true)} disabled={!canModify}
          className="bg-[#FF7819] hover:bg-[#e56811] text-white"
          data-testid="cm-btn-new-quantity"
        >
          <Ruler className="w-4 h-4 mr-1" /> Nuova variazione quantità
        </Button>
        <Button
          variant="outline" onClick={openNewWorkWizard} disabled={!canModify || openingNewWork}
          data-testid="cm-btn-new-work"
        >
          {openingNewWork ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <FilePlus2 className="w-4 h-4 mr-1" />}
          Nuova lavorazione (Offerta extra)
        </Button>
      </div>

      {/* Timeline */}
      <div className="border rounded-lg bg-white">
        <div className="px-3 py-2 border-b bg-slate-50 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700">Timeline modifiche</h3>
          <Button size="sm" variant="ghost" onClick={reload} data-testid="cm-reload"><RefreshCw className="w-3 h-3" /></Button>
        </div>
        {loading && <div className="p-3"><Skeleton className="h-16 w-full" /></div>}
        {!loading && mods.length === 0 && (
          <p className="p-4 text-sm text-slate-500" data-testid="cm-empty">Nessuna modifica registrata. Il contratto è alla revisione iniziale.</p>
        )}
        {!loading && mods.length > 0 && (
          <ul className="divide-y" data-testid="cm-timeline">
            {mods.map(m => (
              <li key={m.id} className="p-3 hover:bg-slate-50 cursor-pointer flex items-center gap-3"
                  onClick={() => setDetailId(m.id)} data-testid={`cm-row-${m.modification_number}`}>
                <GitCommit className="w-4 h-4 text-teal-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{m.modification_number}
                    <span className="ml-2 text-xs text-slate-500">{MODIFICATION_KIND_LABELS[m.kind] || m.kind}</span>
                  </div>
                  <div className="text-xs text-slate-500">
                    {fmtDate(m.effective_at || m.created_at)} · {m.created_by || '—'}
                    {m.target_revision && <> · genera <Badge className="ml-1 bg-teal-100 text-teal-800 border-teal-400">Rev.{m.target_revision}</Badge></>}
                  </div>
                </div>
                <div className="text-right">
                  <Badge className={MODIFICATION_STATUS_COLORS[m.status] || 'bg-slate-100'}>{MODIFICATION_STATUS_LABELS[m.status]}</Badge>
                  <div className={`text-xs mt-1 ${Number(m.total_amount_delta) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {Number(m.total_amount_delta) > 0 ? '+' : ''}{fmtEUR(m.total_amount_delta)}
                  </div>
                </div>
                {/* F.6.4 · Icon-button "Scarica PDF" per modifiche EFFECTIVE.
                    Non altera layout: appoggiato a destra del blocco delta. */}
                {m.status === 'effective' && (
                  <button
                    type="button"
                    onClick={(ev) => handleDownloadPdf(m, ev)}
                    disabled={pdfBusyId === m.id}
                    title="Scarica PDF"
                    aria-label={`Scarica PDF ${m.modification_number}`}
                    className="ml-2 p-1.5 rounded hover:bg-teal-50 text-teal-700 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                    data-testid={`cm-row-download-pdf-${m.modification_number}`}
                  >
                    {pdfBusyId === m.id
                      ? <RefreshCw className="w-4 h-4 animate-spin" />
                      : <Download className="w-4 h-4" />}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Wizard QV */}
      <QuantityVariationWizard
        open={qvOpen} onOpenChange={setQvOpen} subcontract={subcontract}
        onCompleted={() => { reload(); onSubcontractChanged?.(); }}
      />

      {/* Dettaglio modifica */}
      <ContractModificationDetailDialog
        modificationId={detailId} snapshots={snaps}
        onOpenChange={(v) => !v && setDetailId(null)}
        onDownloadPdf={handleDownloadPdf}
        pdfBusyId={pdfBusyId}
      />

      {/* F.6.2A · Wizard Offerta EXTRA (Nuova lavorazione) — nessuna bozza pre-creata.
          Il documento nasce solo al submit finale con >= 1 item. */}
      {newWorkWizardOpen && newWorkProject && (
        <SubcontractOfferWizard
          open={newWorkWizardOpen}
          onOpenChange={(v) => { if (!v) { setNewWorkWizardOpen(false); setNewWorkProject(null); } }}
          project={newWorkProject}
          isAdmin={false}
          editingOffer={null}
          parentSubcontractId={subcontract.id}
          presetSupplierId={subcontract.supplier_id}
          onCreated={handleNewWorkOfferCreated}
        />
      )}

      {/* Offerta EXTRA (dopo creazione) — riusa il componente Offerta esistente
          per portare la bozza fino a contrattualizzazione. */}
      {newWorkOfferId && (
        <SubcontractOfferDetail
          offerId={newWorkOfferId} open={!!newWorkOfferId}
          onOpenChange={(v) => { if (!v) { setNewWorkOfferId(null); reload(); onSubcontractChanged?.(); } }}
          onChanged={() => reload()}
        />
      )}
    </div>
  );
};

const SummaryCard = ({ label, value }) => (
  <div className="rounded-md border bg-white p-3">
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-lg font-semibold mt-0.5">{value}</div>
  </div>
);

const ContractModificationDetailDialog = ({ modificationId, snapshots, onOpenChange, onDownloadPdf, pdfBusyId }) => {
  const { isAdmin } = useAuth();
  const [mod, setMod] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!modificationId) { setMod(null); return; }
    setLoading(true);
    getModification(modificationId).then(setMod).catch(() => toast.error('Errore caricamento modifica')).finally(() => setLoading(false));
  }, [modificationId]);
  const linkedSnap = mod?.generated_snapshot_id
    ? snapshots.find(s => s.id === mod.generated_snapshot_id) : null;
  const isBusy = mod && pdfBusyId === mod.id;
  const canDownload = mod?.status === 'effective';
  // F.6.4 · Preview HTML solo per admin (o dev): endpoint QA, non user-facing.
  const showPreview = canDownload && (isAdmin || process.env.NODE_ENV === 'development');

  return (
    <Dialog open={!!modificationId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="cm-detail-dialog">
        <DialogHeader>
          <DialogTitle>{mod?.modification_number || 'Dettaglio modifica'}</DialogTitle>
        </DialogHeader>
        {loading && <Skeleton className="h-32 w-full" />}
        {!loading && mod && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <Info label="Tipo" value={MODIFICATION_KIND_LABELS[mod.kind] || mod.kind} />
              <Info label="Stato" value={<Badge className={MODIFICATION_STATUS_COLORS[mod.status]}>{MODIFICATION_STATUS_LABELS[mod.status]}</Badge>} />
              <Info label="Revisione generata" value={mod.generated_revision ? <Badge className="bg-teal-100 text-teal-800 border-teal-400" data-testid="cm-detail-revision">Rev.{mod.generated_revision}</Badge> : '—'} />
              <Info label="Δ Importo" value={<span data-testid="cm-detail-delta">{fmtEUR(mod.total_amount_delta)}</span>} />
              <Info label="Efficace il" value={fmtDate(mod.effective_at)} />
              <Info label="Creata da" value={mod.created_by || '—'} />
            </div>
            {mod.reason && <div><strong>Motivazione:</strong> {mod.reason}</div>}
            {mod.notes && <div><strong>Note:</strong> {mod.notes}</div>}

            {/* Snapshot collegato */}
            <div className="rounded border bg-slate-50 p-2 text-xs" data-testid="cm-detail-snapshot">
              <div className="font-medium text-slate-700 mb-1">Snapshot collegato</div>
              {linkedSnap ? (
                <div>
                  ID: <span className="font-mono">{linkedSnap.id.slice(0, 8)}…</span> · Rev.{linkedSnap.revision} ·
                  Importo: {fmtEUR(linkedSnap.state?.total_amount_current)} · {fmtDate(linkedSnap.created_at)}
                </div>
              ) : (
                <div className="text-slate-500">Nessuno snapshot associato.</div>
              )}
            </div>

            {/* F.6.4 · Sezione PDF (F.6.3 endpoints) */}
            <div className="rounded border bg-white p-3 flex items-center gap-2" data-testid="cm-detail-pdf-actions">
              <FileText className="w-4 h-4 text-teal-700 shrink-0" />
              <div className="flex-1 text-xs text-slate-600">
                {canDownload
                  ? <>PDF appendice pronto (Rev.{mod.target_revision}).</>
                  : <>PDF disponibile solo per modifiche efficaci.</>}
              </div>
              {canDownload && (
                <Button
                  size="sm"
                  variant="default"
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                  onClick={() => onDownloadPdf?.(mod)}
                  disabled={!!isBusy}
                  data-testid="cm-detail-download-pdf"
                >
                  {isBusy
                    ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" />Download…</>
                    : <><Download className="w-4 h-4 mr-1" />Scarica PDF</>}
                </Button>
              )}
              {showPreview && (
                <a
                  href={previewModificationHtmlUrl(mod.id)}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center px-2 py-1 rounded text-xs border text-slate-700 hover:bg-slate-50"
                  data-testid="cm-detail-preview-html"
                  title="Anteprima HTML (admin/dev)"
                >
                  <ExternalLink className="w-3 h-3 mr-1" />Anteprima
                </a>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="cm-detail-close">Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Info = ({ label, value }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className="mt-0.5">{value}</div>
  </div>
);

export default ContractModificationsTab;
